import { motion, useMotionValue, useMotionTemplate } from 'framer-motion'
import { useState, type MouseEvent, type ReactNode } from 'react'
import { Marquee } from './ui/marquee'
import { useIsMobile } from '../lib/useIsMobile'
import { useT } from '../i18n/LocaleContext'

/* -------------------- Backgrounds -------------------- */

const CARRIER_LOGOS = [
  { file: 'DHL.svg', name: 'DHL' },
  { file: 'ups.svg', name: 'UPS' },
  { file: 'Fedex.svg', name: 'FedEx' },
  { file: 'DPD.svg', name: 'DPD' },
  { file: 'Gls.svg', name: 'GLS' },
  { file: 'Bpost.svg', name: 'bpost' },
  { file: 'PostNl.svg', name: 'PostNL' },
  { file: 'mondial relay.svg', name: 'Mondial Relay' },
]

function BgCarriers() {
  return (
    <div className="absolute inset-0 flex items-end sm:items-center pb-4 sm:pb-0">
      <Marquee
        duration={40}
        gap="0.75rem"
        className="[mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]"
      >
        {CARRIER_LOGOS.map((c) => (
          <motion.div
            key={c.name}
            whileHover={{ y: -3, scale: 1.03 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-center h-14 min-w-[140px] rounded-xl border border-ink-100 bg-ink-100 px-4 shrink-0 shadow-[0_4px_14px_-6px_rgba(59,93,255,0.18)]"
          >
            <img
              src={`/carriers/${encodeURI(c.file)}`}
              alt={c.name}
              draggable={false}
              className="max-h-7 w-auto object-contain"
            />
          </motion.div>
        ))}
      </Marquee>
    </div>
  )
}

function BgTariff() {
  // Dégressivité relative (pas de prix absolu : hors contexte « tout compris »,
  // un montant brut se lit comme un prix d'appel et perd le visiteur).
  const tiers = [
    { vol: '100', price: '', h: 38, color: 'from-ink-200 to-white', label: 'text-ink-500' },
    { vol: '2K', price: '−4 %', h: 70, color: 'from-accent-300 to-accent-200', label: 'text-accent-700' },
    { vol: '5K+', price: '−8 %', h: 105, color: 'from-accent-700 to-accent-500', label: 'text-accent-700' },
  ]
  return (
    <div className="absolute inset-0 [mask-image:linear-gradient(to_top,black_50%,transparent_95%)]">
      {/* trend line behind bars */}
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 240 200">
        <defs>
          <linearGradient id="trend" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#3b5dff" stopOpacity="0" />
            <stop offset="50%" stopColor="#3b5dff" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#3b5dff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          d="M40 145 Q120 100 200 55"
          stroke="url(#trend)"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          fill="none"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        />
      </svg>
      <div className="absolute inset-0 flex items-end justify-center gap-5 pb-12 px-6">
        {tiers.map((t, i) => (
          <div key={t.vol} className="flex flex-col items-center gap-2">
            {t.price && (
              <motion.span
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.12 }}
                className={`text-[10px] font-semibold tracking-tight ${t.label}`}
              >
                {t.price}
              </motion.span>
            )}
            <div className="relative flex flex-col-reverse">
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: t.h }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className={`w-10 rounded-t-md bg-gradient-to-t ${t.color} ${
                  i === 2 ? 'shadow-[0_-10px_30px_-12px_rgba(59,93,255,0.6)]' : ''
                }`}
              />
            </div>
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 + i * 0.12 }}
              className="text-[10px] font-medium text-ink-500"
            >
              {t.vol}/mois
            </motion.span>
          </div>
        ))}
      </div>
    </div>
  )
}

function BgPackaging() {
  const colors = [
    { c: '#0e0e14', delay: 0.2 },
    { c: '#3b5dff', delay: 0.32 },
    { c: '#e5e5e7', delay: 0.44 },
    { c: '#0e0e14', delay: 0.56 },
  ]
  return (
    <div className="absolute inset-0 flex items-end justify-center gap-2 pb-8">
      {colors.map((c, i) => (
        <motion.div
          key={i}
          initial={{ y: 70, opacity: 0, rotate: -6 }}
          whileInView={{ y: 0, opacity: 1, rotate: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ delay: c.delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -6, rotate: i % 2 === 0 ? -3 : 3 }}
          className="relative w-16 h-24 rounded-lg shadow-[0_18px_38px_-16px_rgba(14,14,20,0.45)]"
          style={{ background: c.c }}
        >
          {i === 1 && (
            <span className="absolute inset-0 rounded-lg ring-1 ring-white/10" />
          )}
        </motion.div>
      ))}
    </div>
  )
}

/**
 * The invoice that collapses to one line.
 *
 * Previously a giant "−20%" in white-on-white gradient text: invisible on the
 * card, and a savings claim the client retired from the home in July. The card
 * sells *all-in*, so the visual now shows the fee lines a 3PL invoice usually
 * carries — struck through at 0 € — resolving into the single line actually
 * billed. Deliberately not another volume curve: the neighbouring "Tarifs
 * alignés à votre volume" card already owns degressivity.
 */
function BgSavings() {
  const { t } = useT()
  const struck = [t('features.savingsStrike1'), t('features.savingsStrike2'), t('features.savingsStrike3')]

  return (
    <div className="absolute inset-x-0 bottom-0 px-6 pb-7 sm:px-7 sm:pb-8">
      <div className="mx-auto w-full max-w-[19rem]">
        {struck.map((label, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay: 0.15 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-baseline justify-between gap-3 py-[0.3rem]"
          >
            <span className="text-[0.8125rem] leading-tight text-ink-500 line-through decoration-ink-400">
              {label}
            </span>
            <span className="shrink-0 text-[0.8125rem] font-medium text-ink-500 line-through decoration-ink-400">
              0 €
            </span>
          </motion.div>
        ))}

        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-2 mb-3 h-px origin-left bg-ink-200"
        />

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.62, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center justify-between gap-3 rounded-xl bg-ink-950 px-4 py-3 shadow-[0_18px_38px_-20px_rgba(14,14,20,0.55)]"
        >
          <span className="text-[0.8125rem] font-medium leading-tight text-white">
            {t('features.savingsTotalLabel')}
          </span>
          <span className="shrink-0 rounded-full bg-accent-500 px-2.5 py-1 text-[0.6875rem] font-semibold tracking-tight text-white">
            {t('features.savingsTotalBadge')}
          </span>
        </motion.div>
      </div>
    </div>
  )
}

/**
 * The badge used to read "24/7", which contradicted the card's own title once
 * the support hours were corrected to the real Mo-Fr 07:00-22:00. It now shows
 * the average reply time — a claim the contact page already makes, and a
 * stronger one: availability is a promise, response time is a measurement.
 */
function BgPulse() {
  const { t } = useT()
  return (
    <div className="absolute inset-0">
      <div className="absolute left-1/2 top-[72%] sm:top-[64%] -translate-x-1/2 -translate-y-1/2 w-24 h-24">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{ scale: [1, 2.6], opacity: [0.5, 0] }}
            transition={{ duration: 2.6, repeat: Infinity, delay: i * 0.85, ease: 'easeOut' }}
            className="absolute inset-0 rounded-full bg-accent-500/20"
          />
        ))}
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="relative w-24 h-24 rounded-full bg-gradient-to-br from-accent-700 to-accent-500 flex items-center justify-center shadow-[0_24px_60px_-15px_rgba(59,93,255,0.7)] overflow-hidden"
        >
          <span className="absolute inset-0 rounded-full ring-1 ring-white/25" />
          <motion.span
            animate={{ x: ['-110%', '110%'] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.5 }}
            className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
          />
          <span className="relative text-white font-semibold text-base tracking-tight">
            {t('features.supportBadge')}
          </span>
        </motion.div>
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="absolute inset-x-0 top-full mt-4 block text-center text-[0.6875rem] font-medium tracking-tight text-ink-500 whitespace-nowrap"
        >
          {t('features.supportBadgeLabel')}
        </motion.span>
      </div>
    </div>
  )
}

/* -------------------- Card -------------------- */

interface FeatureCardProps {
  name: string
  description: string
  highlight: string
  background: ReactNode
  className?: string
  delay?: number
  cta?: { label: string; href: string }
}

function FeatureCard({
  name,
  description,
  highlight,
  background,
  className = '',
  delay = 0,
  cta,
}: FeatureCardProps) {
  const isMobile = useIsMobile()
  const [hover, setHover] = useState(false)
  const mouseX = useMotionValue(-500)
  const mouseY = useMotionValue(-500)
  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    if (isMobile) return
    const r = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - r.left)
    mouseY.set(e.clientY - r.top)
  }
  const onLeave = () => {
    if (isMobile) return
    setHover(false)
    mouseX.set(-500)
    mouseY.set(-500)
  }
  const spotlight = useMotionTemplate`radial-gradient(420px circle at ${mouseX}px ${mouseY}px, rgba(59,93,255,0.16), transparent 65%)`

  return (
    <motion.div
      initial={{ opacity: 0, y: 36, scale: 0.97, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => !isMobile && setHover(true)}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`group relative col-span-3 flex flex-col justify-between overflow-hidden rounded-2xl border border-ink-100 bg-white shadow-[0_2px_4px_rgba(14,14,20,0.04),0_12px_30px_-12px_rgba(14,14,20,0.06)] hover:border-accent-200 hover:shadow-[0_28px_70px_-26px_rgba(59,93,255,0.45)] transition-[border-color,box-shadow] duration-300 ${className}`}
    >
      {/* corner halo */}
      {isMobile ? (
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-25"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.22), rgba(59,93,255,0) 70%)',
          }}
        />
      ) : (
        <motion.div
          aria-hidden
          animate={{ opacity: hover ? 0.85 : 0.25, scale: hover ? 1.15 : 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.22), rgba(59,93,255,0) 70%)',
            filter: 'blur(20px)',
          }}
        />
      )}

      {/* cursor spotlight — desktop only */}
      {!isMobile && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ background: spotlight }}
        />
      )}

      {/* background visual */}
      <div className="absolute inset-0 overflow-hidden">{background}</div>

      {/* foreground content */}
      <div className="pointer-events-none relative z-10 flex flex-col gap-1 p-6 transition-transform duration-300 group-hover:-translate-y-2">
        <span className="inline-flex w-max items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50/90 backdrop-blur-sm text-[10px] px-2.5 py-0.5 font-semibold tracking-wide mb-1.5 text-accent-800">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600" />
          </span>
          {highlight}
        </span>
        <h3 className="text-xl font-semibold tracking-tight text-ink-950">{name}</h3>
        <p className="max-w-lg text-sm text-ink-600 leading-relaxed">{description}</p>
      </div>

      {cta && (
        <div className="pointer-events-none absolute bottom-0 z-10 flex w-full translate-y-10 transform-gpu flex-row items-center p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <a
            href={cta.href}
            className="pointer-events-auto inline-flex items-center gap-1.5 text-sm font-medium text-accent-700 hover:text-accent-800"
          >
            {cta.label}
            <motion.svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              animate={{ x: hover ? 4 : 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </motion.svg>
          </a>
        </div>
      )}

      {/* fade overlay on hover */}
      <div className="pointer-events-none absolute inset-0 transition-all duration-300 group-hover:bg-accent-500/[0.02]" />
    </motion.div>
  )
}

/* -------------------- Section -------------------- */

export default function Features() {
  const isMobile = useIsMobile()
  const { t } = useT()
  return (
    <section id="features" className="relative isolate py-28 overflow-hidden">
      {/* Background DA */}
      <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-40"
          style={{
            background: 'linear-gradient(to bottom, rgba(59,93,255,0.04), transparent)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(ellipse at center, black 25%, transparent 80%)',
          }}
        />
        {/* Two breathing radials — desktop only (blur 60-70px is brutal on mobile GPUs) */}
        {!isMobile && (
          <>
            <motion.div
              animate={{ opacity: [0.28, 0.5, 0.28] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute right-[8%] top-1/4 w-[500px] h-[500px] rounded-full"
              style={{
                background:
                  'radial-gradient(ellipse, rgba(59,93,255,0.16), rgba(59,93,255,0.04) 45%, transparent 75%)',
                filter: 'blur(60px)',
              }}
            />
            <motion.div
              animate={{ opacity: [0.22, 0.45, 0.22] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
              className="absolute left-[5%] bottom-1/4 w-[420px] h-[420px] rounded-full"
              style={{
                background:
                  'radial-gradient(ellipse, rgba(59,93,255,0.13), rgba(59,93,255,0.03) 45%, transparent 75%)',
                filter: 'blur(70px)',
              }}
            />
          </>
        )}
        <div
          className="absolute inset-x-0 -bottom-1 h-32"
          style={{
            background: 'linear-gradient(to bottom, transparent, rgba(14,14,20,0.03))',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{t('features.badgeLabel')}</span>
            <span className="text-accent-400">·</span>
            <span className="text-accent-700">{t('features.badgeTagline')}</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-semibold tracking-[-0.02em] text-ink-950 text-[clamp(2rem,4.5vw,3.25rem)] leading-[1.05]"
          >
            {t('features.headlinePart1')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('features.headlinePart2')}
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.18 }}
            className="mt-5 text-ink-600 text-base sm:text-lg leading-relaxed"
          >
            {t('features.subhead')}
          </motion.p>
        </div>

        <div className="mt-14 grid w-full auto-rows-[24rem] sm:auto-rows-[22rem] grid-cols-3 gap-4">
          <FeatureCard
            name={t('features.carriersName')}
            description={t('features.carriersDesc')}
            highlight={t('features.carriersHighlight')}
            cta={{ label: t('features.carriersCta'), href: '#integrations' }}
            delay={0}
            className="col-span-3 lg:col-span-2"
            background={<BgCarriers />}
          />
          <FeatureCard
            name={t('features.tariffName')}
            description={t('features.tariffDesc')}
            highlight={t('features.tariffHighlight')}
            delay={0.05}
            className="col-span-3 lg:col-span-1"
            background={<BgTariff />}
          />
          <FeatureCard
            name={t('features.packagingName')}
            description={t('features.packagingDesc')}
            highlight={t('features.packagingHighlight')}
            delay={0.1}
            className="col-span-3 lg:col-span-1"
            background={<BgPackaging />}
          />
          <FeatureCard
            name={t('features.savingsName')}
            description={t('features.savingsDesc')}
            highlight={t('features.savingsHighlight')}
            delay={0.15}
            className="col-span-3 lg:col-span-1"
            background={<BgSavings />}
          />
          <FeatureCard
            name={t('features.supportName')}
            description={t('features.supportDesc')}
            highlight={t('features.supportHighlight')}
            delay={0.2}
            className="col-span-3 lg:col-span-1"
            background={<BgPulse />}
          />
        </div>
      </div>
    </section>
  )
}
